import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Save, Sun, Zap } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../hooks/useConfirm";

const FormProfile = ({ user, isLoading, refetchUser }) => {
    const { t } = useTranslation();
    const [confirm, ConfirmDialog] = useConfirm();
    const [fields, setFields] = React.useState({
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
        contactNumber: user?.contactNumber,
        companyName: user?.company?.name,
        interfaceStyle: user?.interfaceStyle || "simple",
    });

    // Update fields when user ID changes (not on every re-render)
    // We intentionally only depend on user?.id to avoid resetting fields
    // when the user object reference changes due to language changes or other re-renders
    React.useEffect(() => {
        if (user) {
            setFields({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                contactNumber: user.contactNumber,
                companyName: user.company?.name,
                interfaceStyle: user.interfaceStyle || "simple",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const handleChange = (e) => {
        setFields({ ...fields, [e.target.name]: e.target.value });
    };

    const { call: updateUser } = useBluelagoon({
        method: "updateUser",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dataToUpdate = {
            id: user.id,
            ...fields,
        };
        delete dataToUpdate.email;
        await updateUser({
            id: user.id,
            ...dataToUpdate,
        });

        refetchUser();

        toast.success(t("profile.profileUpdated"));
    };

    if (isLoading) {
        return (
            <form>
                <div className="space-y-4">
                    <h2 className="font-semibold text-xl">
                        {t("profile.title")}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label>
                                <div className="animate-pulse w-20 h-5 bg-gray-200 mb-1 "></div>
                            </Label>
                            <div className="animate-pulse w-100 h-10 bg-gray-200  "></div>
                        </div>
                        <div>
                            <Label>
                                <div className="animate-pulse w-20 h-5 bg-gray-200 mb-1 "></div>
                            </Label>
                            <div className="animate-pulse w-100 h-10 bg-gray-200  "></div>
                        </div>
                        <div>
                            <Label>
                                <div className="animate-pulse w-20 h-5 bg-gray-200 mb-1 "></div>
                            </Label>
                            <div className="animate-pulse w-100 h-10 bg-gray-200  "></div>
                        </div>
                        <div>
                            <Label>
                                <div className="animate-pulse w-20 h-5 bg-gray-200 mb-1 "></div>
                            </Label>
                            <div className="animate-pulse w-100 h-10 bg-gray-200  "></div>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <div className="animate-pulse w-40 h-12 bg-gray-200 mb-1 "></div>
                    </div>
                </div>
            </form>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="font-semibold text-xl">
                        {t("profile.title")}
                    </h2>
                    <LanguageSwitcher />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label>{t("profile.firstName")}</Label>
                        <Input
                            placeholder={t("profile.firstNamePlaceholder")}
                            name="firstName"
                            value={fields.firstName}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <Label>{t("profile.lastName")}</Label>
                        <Input
                            placeholder={t("profile.lastNamePlaceholder")}
                            name="lastName"
                            value={fields.lastName}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <Label>{t("profile.email")}</Label>
                        <Input
                            placeholder={t("profile.emailPlaceholder")}
                            name="email"
                            value={fields.email}
                            disabled
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <Label>{t("profile.phone")}</Label>
                        <Input
                            placeholder={t("profile.phonePlaceholder")}
                            name="contactNumber"
                            value={fields.contactNumber}
                            onChange={handleChange}
                        />
                    </div>
                    <div>
                        <Label>{t("profile.companyName")}</Label>
                        <Input
                            placeholder={t("profile.companyNamePlaceholder")}
                            name="companyName"
                            value={fields.companyName}
                            onChange={handleChange}
                        />
                    </div>
                    {/* <div>
                        <Label>{t("profile.interfaceStyle")}</Label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() =>
                                    setFields({
                                        ...fields,
                                        interfaceStyle: "simple",
                                    })
                                }
                                className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-colors ${
                                    fields.interfaceStyle === "simple"
                                        ? "bg-blue-50 border-blue-200 text-blue-700"
                                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <Sun className="w-4 h-4" />
                                {t("profile.simpleMode")}
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        await confirm({
                                            title: t(
                                                "profile.expertModeWarningTitle"
                                            ),
                                            description: t(
                                                "profile.expertModeWarningDescription"
                                            ),
                                            confirmText: t(
                                                "profile.switchToExpert"
                                            ),
                                            cancelText: t("common.cancel"),
                                        });
                                        setFields({
                                            ...fields,
                                            interfaceStyle: "expert",
                                        });
                                    } catch (error) {
                                        // User cancelled, do nothing
                                    }
                                }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-colors ${
                                    fields.interfaceStyle === "expert"
                                        ? "bg-blue-50 border-blue-200 text-blue-700"
                                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <Zap className="w-4 h-4" />
                                {t("profile.expertMode")}
                            </button>
                        </div>
                    </div> */}
                </div>
                <div className="flex justify-end">
                    <Button size="lg" className="w-full md:w-min">
                        <Save />
                        {t("profile.save")}
                    </Button>
                </div>
            </div>
            <ConfirmDialog />
        </form>
    );
};

export default FormProfile;
